import React, { useEffect, useRef, useState } from 'react';
import Webcam from 'react-webcam';
import * as faceapi from 'face-api.js';
import { useSearchParams } from 'react-router-dom';
import { CARTERA_API_HOST, universalApi } from '../../../api/api';
import Tick from '../../../assets/icons/Tick';
import DefaultButton from '../../../components/default-button/DefaultButton';
import { simpleGetter } from '../../../actions/common';

export const FaceMatch = () => {
    const webcamRef = useRef<Webcam>(null);
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [status, setStatus] = useState('Загрузка моделей...');
    const [match, setMatch] = useState<null | boolean>(null);
    const [isReady, setIsReady] = useState(false);
    const centeredCounterRef = useRef(0);
    const detectionIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const loadModels = async () => {
        const MODEL_URL = '/models';
        await Promise.all([
            faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL),
            faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
            faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        ]);
        setStatus('Пожалуйста, расположите лицо в центре кадра');
        setIsReady(true);
    };

    const getSavedFaceDescriptor = async (): Promise<faceapi.LabeledFaceDescriptors | null> => {
        try {
            const res = await fetch(`${CARTERA_API_HOST}/api/v1/users/self`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            const imageUrl = data.item?.scanned_face_image;
            if (!imageUrl) return null;

            const imageResponse = await fetch(imageUrl);
            const blob = await imageResponse.blob();
            const image = await faceapi.bufferToImage(blob);
            const detection = await faceapi.detectSingleFace(image).withFaceLandmarks().withFaceDescriptor();
            if (!detection) return null;

            return new faceapi.LabeledFaceDescriptors('saved-face', [detection.descriptor]);
        } catch (e) {
            console.error('Error loading saved face:', e);
            return null;
        }
    };

    const matchLiveFace = async () => {
        setStatus('Сравниваем лицо...');
        const screenshot = webcamRef.current?.getScreenshot();
        if (!screenshot) return setStatus('Не удалось захватить изображение с камеры.');

        const img = await faceapi.fetchImage(screenshot);
        const liveDetection = await faceapi.detectSingleFace(img).withFaceLandmarks().withFaceDescriptor();
        if (!liveDetection) {
            setStatus('Лицо не обнаружено. Попробуйте снова.');
            return;
        }

        const savedDescriptor = await getSavedFaceDescriptor();
        if (!savedDescriptor) {
            setStatus('Не удалось загрузить сохраненное лицо');
            return;
        }

        const matcher = new faceapi.FaceMatcher(savedDescriptor, 0.5);
        const bestMatch = matcher.findBestMatch(liveDetection.descriptor);

        if (bestMatch.label === 'saved-face') {
            setMatch(true);
            await fetch(`${CARTERA_API_HOST}/api/v1/new-universal/MainUser/update`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, "content-type": "application/json", },
                body: JSON.stringify({
                    filters: {},
                    update: {
                        last_matched_at: new Date().toISOString()
                    }
                })
            });
            setStatus('Поздравляем! Вы успешно верифицировали свою личность');
        } else {
            setMatch(false);
            setStatus('Лицо не совпадает. Попробуйте снова.');
        }
    };

    const checkCenteredFace = async () => {
        const screenshot = webcamRef.current?.getScreenshot();
        if (!screenshot) return;

        const img = await faceapi.fetchImage(screenshot);
        const detection = await faceapi.detectSingleFace(img);

        if (!detection) {
            centeredCounterRef.current = 0;
            setStatus('Лицо не обнаружено');
            return;
        }

        const box = detection.box;
        const centerX = box.x + box.width / 2;
        const centerY = box.y + box.height / 2;

        const frameWidth = img.width;
        const frameHeight = img.height;

        const centerRegion = {
            xMin: frameWidth * 0.3,
            xMax: frameWidth * 0.7,
            yMin: frameHeight * 0.3,
            yMax: frameHeight * 0.7,
        };

        const isCentered =
            centerX >= centerRegion.xMin &&
            centerX <= centerRegion.xMax &&
            centerY >= centerRegion.yMin &&
            centerY <= centerRegion.yMax;

        if (isCentered) {
            centeredCounterRef.current += 1;
            setStatus('Лицо в центре, захват...');
        } else {
            centeredCounterRef.current = 0;
            setStatus('Пожалуйста, расположите лицо в центре кадра');
        }

        // If face is centered for 2+ checks (e.g. ~1s)
        if (centeredCounterRef.current >= 2) {
            clearInterval(detectionIntervalRef.current!);
            await matchLiveFace();
        }
    };

    useEffect(() => {
        loadModels();
    }, []);

    useEffect(() => {
        if (isReady && match === null) {
            detectionIntervalRef.current = setInterval(checkCenteredFace, 500);
        }
        return () => {
            if (detectionIntervalRef.current) clearInterval(detectionIntervalRef.current);
        };
    }, [isReady, match]);

    return (
        <div className='w-full h-full min-h-screen flex flex-col'>
            <div className='flex flex-row px-4 py-4 shadow-[0px_0px_8px_0px_rgba(46,68,119,0.12)]'>
                <span className='font-medium text-[22px] leading-7 text-valley-black'>
                    Верификация лица
                </span>
            </div>

            <div className='flex flex-row flex-1 justify-center items-center relative'>
                {match === true ? (
                    <div className='flex flex-col px-4 text-center gap-3'>
                        <Tick additionalStyle='text-valley-dark-green' />
                        <span className='font-semibold text-lg text-valley-black'>
                            {status}
                        </span>
                        <a href='kbtu-uniq://' className='flex items-center gap-1 py-1 border rounded-lg font-semibold bg-valley-main max-h-fit justify-center text-white'>
                            Вернуться в приложение
                        </a>
                    </div>
                ) : (
                    <>
                        {isReady && (
                            <div className='absolute w-full flex flex-row items-center justify-center top-10 px-4'>
                                <div className='flex flex-row items-center justify-center text-center px-4 py-2 bg-valley-black shadow-[0px_0px_8px_0px_rgba(46,68,119,0.12)]'>
                                    <span className='font-medium text-white'>
                                        {status}
                                    </span>
                                </div>
                            </div>
                        )}

                        <Webcam
                            ref={webcamRef}
                            screenshotFormat="image/jpeg"
                            videoConstraints={{ facingMode: 'user' }}
                            className="rounded-lg"
                            style={{
                                width: '100%',
                                height: 'calc(100vh - 160px)',
                                objectFit: 'cover',
                            }}
                        />

                        {match === null && (
                            <div
                                className="absolute border-4 border-green-500 rounded-md"
                                style={{
                                    width: '40%',
                                    height: '40%',
                                    top: '30%',
                                    left: '30%',
                                    pointerEvents: 'none',
                                    boxShadow: '0 0 0 9999px rgba(0,0,0,0.3)', // darken outside
                                    transition: 'all 0.3s ease-in-out',
                                }}
                            />
                        )}

                        {/* Optional manual fallback */}
                        <div className='absolute bottom-6 left-0 right-0 flex justify-center'>
                            <DefaultButton
                                buttonText='Сравнить лицо вручную'
                                onClick={matchLiveFace}
                            />
                        </div>

                        {match === false && (
                            <div className='absolute bottom-20 w-full text-center text-red-600 font-medium'>
                                {status}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}